import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, withLatestFrom } from 'rxjs/operators';
import { of, from } from 'rxjs';
import { ClientService } from '../../../core/services/client.service';
import { ClientActions } from './client.actions';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { getQueries } from './client.selectors';
import { AlertService } from 'src/app/core/services/alert.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { HttpErrorResponse } from '@angular/common/http';

@Injectable()
export class ClientEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly clientService: ClientService,
    private readonly store: Store,
    private readonly alertService: AlertService,
    private readonly navigationService: NavigationService
  ) {}

  loadClientsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientActions.listClients),
      withLatestFrom(this.store.select(getQueries)),
      mergeMap(([, filters]) => {
        return from(
          this.clientService.listClients(
            filters.body ?? {},
            filters.params.page,
            filters.params.limit
          )
        ).pipe(
          map((clients) => {
            return ClientActions.listClientsSuccess({
              response: clients,
              filters,
            });
          }),
          catchError((error) => {
            return of(
              ClientActions.listClientsFailure({ error: error.message })
            );
          })
        );
      })
    )
  );

  loadClientNamesList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientActions.listClientNames),
      mergeMap(() => {
        return from(this.clientService.listClientNames()).pipe(
          map((clients) => {
            return ClientActions.listClientNamesSuccess({
              response: clients,
            });
          }),
          catchError((error) => {
            return of(
              ClientActions.listClientNamesFailure({ error: error.message })
            );
          })
        );
      })
    )
  );

  loadAllStatusClientNames$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientActions.loadAllStatusClientNames),
      mergeMap(() =>
        from(this.clientService.listAllStatusClientNames()).pipe(
          map((response) => {
            return ClientActions.loadAllStatusClientNamesSuccess({
              response: response,
            });
          }),
          catchError((error) =>
            of(
              ClientActions.loadAllStatusClientNamesFailure({
                error: error.message,
              })
            )
          )
        )
      )
    )
  );

  loadSingleClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientActions.loadSingleClient),
      mergeMap(({ id }) =>
        from(this.clientService.getClientById(id)).pipe(
          map((client) =>
            ClientActions.loadSingleClientSuccess({ client: client.data })
          ),
          catchError((error) => {
            return of(
              ClientActions.loadSingleClientFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  createClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientActions.createClient),
      mergeMap((action) =>
        from(this.clientService.createClient(action.client)).pipe(
          map((response) => {
            action.closeModalSignal.set(true);
            this.alertService.success(
              response.message,
              'Success',
              response.data.client_name
            );
            return ClientActions.createClientSuccess({ client: response.data });
          }),
          catchError((error) => {
            return of(
              ClientActions.createClientFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  updateClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientActions.updateClient),
      mergeMap((action) =>
        from(this.clientService.updateClient(action.id, action.client)).pipe(
          map((response) => {
            action.closeModalSignal.set(true);
            this.alertService.success(
              response.message,
              'Success',
              response.data.client_name
            );
            return ClientActions.updateClientSuccess({ client: response.data });
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status == 403) {
              action.closeModalSignal.set(true);
            }
            return of(
              ClientActions.updateClientFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  refreshDataOnChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ClientActions.createClientSuccess,
        ClientActions.updateClientSuccess,
        ClientActions.deleteClientSuccess,
        ClientActions.changeClientStatusSuccess
      ),
      withLatestFrom(this.store.select(getQueries)),
      mergeMap(() =>
        of(
          ClientActions.listClients(),
          ClientActions.loadAllStatusClientNames(),
          ClientActions.listClientNames()
        )
      )
    )
  );

  deleteClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientActions.deleteClient),
      mergeMap((action) =>
        from(this.clientService.deleteClient(action.id)).pipe(
          map((response) => {
            action.closeModalSignal.set(true);
            this.alertService.success(
              response.message,
              'Success',
              response.data.client_name
            );
            return ClientActions.deleteClientSuccess({ id: action.id });
          }),
          catchError((error) => {
            return of(
              ClientActions.deleteClientFailure({ error: error.message })
            );
          })
        )
      )
    )
  );

  changeStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientActions.changeClientStatus),
      mergeMap((action) =>
        from(this.clientService.changeClientStatus(action.id)).pipe(
          map((response) => {
            this.alertService.success(
              response.message,
              'Success',
              `${response.data.client_name} `
            );
            return ClientActions.changeClientStatusSuccess();
          }),
          catchError((error) => {
            return of(
              ClientActions.changeClientStatusFailure({ error: error.message })
            );
          })
        )
      )
    )
  );
}
